import { getRequest } from "./ApiService";

const API_URL = process.env.REACT_APP_API_URL;

// In-memory cache keyed by "competitionId-roundId"
// Stores resolved data AND pending promises to avoid duplicate in-flight requests
var _cache = {};
var _pending = {};

// Field name mapping: CAReport field -> MetricsReport field
var CA_FIELD_MAP = {
  average_selling_price_auto: "avg_revenues_per_unit_auto",
  average_selling_price_mobile: "avg_revenues_per_unit_mobile",
  direct_sales_spend: "salesrep",
  average_unit_cost: "average_unit_costs",
  human_utilization: "weighted_labor_utilization_rate",
  machine_utilization: "weighted_machine_utilization_rate",
  market_capitalization: "total_market_capital",
  digital_marketing_spend: "digital_marketing",
  traditional_marketing_spend: "traditional_marketing",
  cost_of_capital: "capm",
};

/**
 * Clear the cache. Call this when round changes or data is submitted.
 */
export function clearMetricsCache() {
  _cache = {};
  _pending = {};
}

/**
 * Fetch all MetricsReport data for a competition + round.
 * Returns a promise that resolves to an array of MetricsReport objects.
 * Results are cached in memory so repeated calls don't hit the API.
 *
 * @param {number} competitionId
 * @param {number} roundId - round ID (not round number)
 * @returns {Promise<Array>}
 */
export function fetchCompetitionMetrics(competitionId, roundId, options) {
  // `include` becomes part of the cache key so responses with promos
  // (from include=product_awareness) don't overwrite the base response
  // and vice versa. Callers that need promos must opt in explicitly.
  var include = (options && options.include) || null;
  var key = (competitionId || "all") + "-" + roundId + "-" + (include || "");
  if (_cache[key]) {
    return Promise.resolve(_cache[key]);
  }
  if (_pending[key]) {
    return _pending[key];
  }

  var url = API_URL + "v2/finance/metricsreports/";
  var sep = "?";
  if (competitionId) {
    url += sep + "competition=" + competitionId;
    sep = "&";
  }
  if (roundId) {
    url += sep + "round=" + roundId;
    sep = "&";
  }
  if (include) {
    url += sep + "include=" + include;
  }

  _pending[key] = getRequest(url).then(function (res) {
    var results = res.body.results || res.body;
    _cache[key] = results;
    delete _pending[key];
    return results;
  });

  return _pending[key];
}

/**
 * Fetch MetricsReport data for a single team.
 * Uses the competition cache if available, otherwise fetches by team.
 *
 * @param {number} teamId
 * @param {number} roundId - round ID (not round number)
 * @returns {Promise<Array>}
 */
export function fetchTeamMetrics(teamId, roundId) {
  // Check if any cached competition data contains this team
  var cacheKeys = Object.keys(_cache);
  for (var i = 0; i < cacheKeys.length; i++) {
    var cached = _cache[cacheKeys[i]];
    var match = cached.filter(function (item) {
      return Number(item.team) === Number(teamId) &&
        Number(item.round) === Number(roundId);
    });
    if (match.length > 0) {
      return Promise.resolve(match);
    }
  }

  // Check if there's a pending competition request that might contain this team
  var pendingKeys = Object.keys(_pending);
  if (pendingKeys.length > 0) {
    return _pending[pendingKeys[0]].then(function (results) {
      var match = results.filter(function (item) {
        return Number(item.team) === Number(teamId) &&
          Number(item.round) === Number(roundId);
      });
      if (match.length > 0) {
        return match;
      }
      // Fallback: fetch by team directly
      var url = API_URL + "v2/finance/metricsreports/?team=" + teamId;
      if (roundId) {
        url += "&round=" + roundId;
      }
      return getRequest(url).then(function (res) {
        return res.body.results || res.body;
      });
    });
  }

  // Fallback: fetch by team directly
  var url = API_URL + "v2/finance/metricsreports/?team=" + teamId;
  if (roundId) {
    url += "&round=" + roundId;
  }

  return getRequest(url).then(function (res) {
    return res.body.results || res.body;
  });
}

/**
 * Map MetricsReport data to CAReport-compatible field names.
 * Use this when replacing competition/competitive_analysis/ calls.
 *
 * @param {Object} metricsReport - single MetricsReport object
 * @returns {Object} - object with CAReport-compatible field names added
 */
export function mapToCompetitiveAnalysis(metricsReport) {
  var mapped = Object.assign({}, metricsReport);
  var caFields = Object.keys(CA_FIELD_MAP);
  for (var i = 0; i < caFields.length; i++) {
    var caField = caFields[i];
    var metricsField = CA_FIELD_MAP[caField];
    if (mapped[metricsField] !== undefined) {
      mapped[caField] = mapped[metricsField];
    }
  }
  // market_demand = sum of auto + mobile
  mapped.market_demand =
    (mapped.market_demand_auto || 0) + (mapped.market_demand_mobile || 0);
  return mapped;
}

/**
 * Fetch competition metrics and map to CAReport-compatible format.
 * Drop-in replacement for competition/competitive_analysis/ calls.
 *
 * @param {number} competitionId
 * @param {number} roundId
 * @returns {Promise<Array>}
 */
export function fetchCompetitiveAnalysis(competitionId, roundId) {
  return fetchCompetitionMetrics(competitionId, roundId).then(function (results) {
    return results.map(mapToCompetitiveAnalysis);
  });
}



// WEBPACK FOOTER //
// ./src/api/MetricsService.js